import React, { createContext, useContext, useMemo, useRef, useState } from "react"
import { toAbsoluteUrl } from "@common-metronic/helpers"
import { useSearchResultsContext } from "../CandidatsRechercheResults"
import { IBrowserListItemPropsModel, ISearchResultsDataItemResponseModel, ISearchResultsListItemContextPropsModel } from "../core/_models"
import { getSelectedCandidatePlusPreviousAndNextCandidate } from "../core/helpers"
import MyLoadingOverlay from "../../../../../../../common/components/MyLoadingOverlay"
import { useSearchResultsListContext } from "./_CandidatsRechercheResultsList"
import {
     AddOrRemoveToFromListeTraitementButton,
     AddToViviersButton,
     BottomCardDivInfoDms,
     DateLastCandidatureOrDateSaisi,
     Disponibilite,
     Mail,
     Metiers,
     Mobilite,
     PartagerProfilParMailButton,
     Phone,
     SalaireNetMensuel,
     SeenSuiviVivierListeTraitementStatutsIcons,
     TagCandidatureSpon,
     TagEmbauche,
} from "./_CandidatsRechercheResultsListItemHelpers"
import { CONST_CANDIDAT_ETAT_EMBAUCHE, CONST_CANDIDAT_TYPE_CS, CONST_CANDIDATSUIVI_TYPE_BLACKLIST_NON_RECOMMANDE } from "@common-constants/*"
import FileAndDriveHelper from "@common-helpers/FileAndDriveHelper"
import MyModal from "@common-utils/MyModal"
import { CandidatSuiviListing } from "../../../suivi/listing/CandidatSuiviListing"
import { useAuth } from "../../../../../../AppContext"
import clsx from "clsx"
import MyTooltip from "@common-utils/MyTooltip"

// Listing for >= MD breakpoint
const ListItem = ({ item, handleClickItemForPreview, isItemSelected, isItemLoading }: IBrowserListItemPropsModel) => {
     const globalContext = useSearchResultsContext()
     const listContext = useSearchResultsListContext()
     const listItemContext = useSearchResultsListItemContext()

     const { currentUser } = useAuth()

     const [isAvatarLoading, setIsAvatarLoading] = useState<boolean>(!!item.candidat.user?.avatarGoogleDriveId)
     const checkboxInputRef = useRef<any>(null)
     const [isHovered, setIsHovered] = useState<boolean>(false)

     // --- BLACKLIST LOGIC ---
     const myPersonnelId = currentUser?.personnel.id
     const myTeamIds = currentUser?.teamRecrutement?.map(p => p.id) || []

     // All blacklist entries regardless of author
     const blacklistEntries = item.candidat.candidatSuivis?.filter(s => s.type === CONST_CANDIDATSUIVI_TYPE_BLACKLIST_NON_RECOMMANDE) || []
     const isBlacklisted = blacklistEntries.length > 0

     // Specifically check if team blacklisted for the grayscale effect
     const isBlacklistedByMeOrTeam = blacklistEntries.some(s => s.personnel?.id === myPersonnelId || (s.personnel?.id && myTeamIds.includes(s.personnel.id)))

     const isItemBeingUpdated = !!globalContext.itemsBeingUpdated.find(item_ => item_.candidat.id === item.candidat.id)

     function handleOnCheckItem() {
          const isItemChecked = globalContext.isBulkSelectionEnabled.checkedElementsInPage.find(
               (item_: ISearchResultsDataItemResponseModel) => item_.candidat.id === item.candidat.id
          )
          if (!isItemChecked) {
               globalContext.setIsBulkSelectionEnabled(prev => ({
                    ...prev,
                    checkedElementsInPage: [...prev.checkedElementsInPage, item],
                    areAllElementsInAllPagesChecked: false,
               }))
          } else {
               globalContext.setIsBulkSelectionEnabled(prev => ({
                    ...prev,
                    checkedElementsInPage: prev.checkedElementsInPage.filter((item_: ISearchResultsDataItemResponseModel) => item_.candidat.id !== item.candidat.id),
                    areAllElementsInAllPagesChecked: false,
               }))
          }
     }

     const UnifiedBlacklistTooltip = () => (
          <div className="p-1 text-start">
               {/* 1. Titre */}
               <div className="fw-bolder text-uppercase fs-9 mb-2 border-bottom border-white border-opacity-25 pb-1">Non recommandé par</div>

               {/* 2. Liste des personnes */}
               <div className="d-flex flex-column gap-1 align-items-center">
                    {blacklistEntries.map((entry, idx) => (
                         <div key={idx} className="fw-bold fs-9 text-info d-flex align-items-center justify-content-center">
                              <i className="fas fa-user-edit me-2 fs-10 text-white-50"></i>
                              {entry.personnel?.prenomNom}
                         </div>
                    ))}
               </div>

               {/* 3. Footer */}
               <div className="text-muted fs-10 mt-3 pt-2 border-top border-white border-opacity-10 fst-italic text-center">
                    <i className="fas fa-mouse-pointer me-1 fs-10"></i>
                    Cliquez pour voir les détails complets
               </div>
          </div>
     )

     return (
          <MyLoadingOverlay isActive={isItemLoading || isItemBeingUpdated}>
               <>
                    <div
                         className={clsx(
                              isItemSelected && "border-start border-2 border-primary",
                              isBlacklisted && "bg-light-danger bg-opacity-10" // Outer div BG if anyone blacklisted
                         )}
                         onClick={e => handleClickItemForPreview(e)}
                         ref={isItemSelected ? listContext.selectedItemRef : null}
                    >
                         <div
                              className={clsx(
                                   "p-4 d-flex align-items-center",
                                   isItemSelected ? (isBlacklisted ? "mb-4 bg-light-danger" : "mb-4 bg-light") : "cursor-pointer", // Row BG if blacklisted
                                   !isItemSelected && isBlacklisted && "bg-light-danger bg-opacity-50"
                              )}
                         >
                              {globalContext.isBulkSelectionEnabled.enabled && (
                                   <div className={"form-check form-check-custom form-check-solid form-check-sm pe-6 ps-2 pt-4 pb-4 not-concerned-by-preview cursor-pointer"}>
                                        <input
                                             type="checkbox"
                                             className={"form-check-input cursor-pointer border border-secondary not-concerned-by-preview"}
                                             style={{ height: "2rem", width: "2rem" }}
                                             checked={!!globalContext.isBulkSelectionEnabled.checkedElementsInPage.find(itm => item.candidat.id === itm.candidat.id)}
                                             onChange={() => handleOnCheckItem()}
                                             ref={checkboxInputRef}
                                        />
                                   </div>
                              )}

                              <div className="d-flex flex-column align-items-center">
                                   <div className="position-relative">
                                        <div className={"d-flex justify-content-center cursor-pointer"} onMouseEnter={() => setIsHovered(true)}>
                                             {globalContext.filters.filters?.viviers && isHovered ? (
                                                  <i
                                                       className={"fas fa-times-circle text-danger d-flex justify-content-center align-items-center cursor-pointer"}
                                                       style={{ height: "50px", width: "50px", borderRadius: "50%", fontSize: "3rem" }}
                                                       onMouseOut={() => setIsHovered(false)}
                                                       onClick={e => {
                                                            e.stopPropagation()
                                                            globalContext.handleRemoveFromViviers([item], globalContext.filters!.filters!.viviers!)
                                                       }}
                                                  />
                                             ) : (
                                                  <MyLoadingOverlay isActive={isAvatarLoading} styles={{ height: "50px", width: "50px", borderRadius: "50%" }} size={"sm"}>
                                                       <img
                                                            src={
                                                                 item.candidat.user?.avatarGoogleDriveId
                                                                      ? FileAndDriveHelper.getThumbnailDriveUrl(item.candidat.user.avatarGoogleDriveId)
                                                                      : toAbsoluteUrl("/media/svg/avatars/blank.svg")
                                                            }
                                                            onLoad={() => setIsAvatarLoading(false)}
                                                            style={{
                                                                 height: "50px",
                                                                 width: "50px",
                                                                 borderRadius: "50%",
                                                                 filter: isBlacklistedByMeOrTeam ? "grayscale(100%)" : "none",
                                                            }}
                                                            alt="Avatar"
                                                       />
                                                  </MyLoadingOverlay>
                                             )}
                                        </div>

                                        {/* Unified fa-ban badge on avatar if blacklisted */}
                                        {isBlacklisted && (
                                             <MyTooltip title={<UnifiedBlacklistTooltip />}>
                                                  <div
                                                       className="position-absolute top-0 start-0 translate-middle badge badge-circle badge-danger border border-white border-2 shadow-sm cursor-pointer"
                                                       style={{ width: "22px", height: "22px", zIndex: 10 }}
                                                       onClick={() =>
                                                            listItemContext.setIsSuivisModalOpen({ isOpen: true, filters: [CONST_CANDIDATSUIVI_TYPE_BLACKLIST_NON_RECOMMANDE] })
                                                       }
                                                  >
                                                       <i className="fas fa-ban text-white fs-10"></i>
                                                  </div>
                                             </MyTooltip>
                                        )}
                                   </div>

                                   <div className="mt-2">
                                        {isItemSelected && item.candidat.type === CONST_CANDIDAT_TYPE_CS && <TagCandidatureSpon />}
                                        {isItemSelected && item.candidat.etat === CONST_CANDIDAT_ETAT_EMBAUCHE && <TagEmbauche />}
                                   </div>
                              </div>

                              <div className={"flex-grow-1 ms-4"}>
                                   <div className={"d-flex align-items-center"}>
                                        <div className="d-flex align-items-center gap-2">
                                             <span className={clsx("fs-5 fw-bold", isBlacklisted ? "text-danger" : "text-gray-900")}>{item.candidat.prenomNom}</span>

                                             {/* Unified fa-ban icon next to name */}
                                             {isBlacklisted && (
                                                  <MyTooltip title={<UnifiedBlacklistTooltip />}>
                                                       <i
                                                            className="fas fa-ban text-danger fs-7 cursor-pointer"
                                                            onClick={() =>
                                                                 listItemContext.setIsSuivisModalOpen({
                                                                      isOpen: true,
                                                                      filters: [CONST_CANDIDATSUIVI_TYPE_BLACKLIST_NON_RECOMMANDE],
                                                                 })
                                                            }
                                                       />
                                                  </MyTooltip>
                                             )}
                                        </div>
                                        <div className={"ms-auto"}>
                                             <DateLastCandidatureOrDateSaisi classNames={"not-concerned-by-preview"} />
                                        </div>
                                   </div>

                                   <div className={"d-flex gap-1 align-items-center"}>
                                        {item.candidat.niveauEtudes && (
                                             <>
                                                  <span className={"text-primary"}>{item.candidat.niveauEtudesLabel}</span>
                                                  <span>-</span>
                                             </>
                                        )}
                                        <Metiers classNames={"not-concerned-by-preview"} />
                                        <div className={"ms-auto"}>
                                             <SeenSuiviVivierListeTraitementStatutsIcons />
                                        </div>
                                   </div>
                              </div>
                         </div>

                         {isItemSelected && (
                              <div className={"d-flex align-items-center px-4 pb-4"}>
                                   <div className={"col-8"}>
                                        <Mail classNames={"mb-2"} />
                                        <Phone classNames={"mb-2"} />
                                        <Disponibilite classNames={"mb-2"} />
                                        <Mobilite classNames={"mb-2"} />
                                        <SalaireNetMensuel classNames={"mb-2"} />
                                   </div>
                                   <div className="col text-end">
                                        <AddOrRemoveToFromListeTraitementButton classNames={"mb-2"} />
                                        <AddToViviersButton classNames={"mb-2"} />
                                        <PartagerProfilParMailButton />
                                   </div>
                              </div>
                         )}
                    </div>
                    {isItemSelected && <BottomCardDivInfoDms />}
               </>
          </MyLoadingOverlay>
     )
}

const SearchResultsListItemContext = createContext<ISearchResultsListItemContextPropsModel>({} as ISearchResultsListItemContextPropsModel)
export const useSearchResultsListItemContext = () => useContext(SearchResultsListItemContext)
const CandidatsRechercheResultsListItem = ({ item, index }: { item: ISearchResultsDataItemResponseModel; index: number }) => {
     const globalContext = useSearchResultsContext()

     const isItemSelected: boolean = globalContext.itemInPreview !== null && globalContext.itemInPreview.selectedItem.candidat.id === item.candidat.id
     const isItemLoading: boolean =
          globalContext.itemInPreview !== null && globalContext.itemInPreview.selectedItem.candidat.id === item.candidat.id && globalContext.itemInPreview.isSelectedItemLoading

     const [isSuivisModalOpen, setIsSuivisModalOpen] = useState<{ isOpen: boolean; filters: string[] }>({
          isOpen: false,
          filters: [],
     })

     // Shows candidate curriculum in preview + prepares quick navigation from one curriculum to another
     function handleClickItemForPreview(e: React.MouseEvent<any>) {
          const clickedElement = e.target as HTMLElement
          // .not-concerned-by-preview elements are not concerned by the click
          if (!clickedElement.classList.contains("not-concerned-by-preview") && globalContext.itemInPreview?.selectedItem.candidat.id !== item.candidat.id) {
               globalContext.setAreFiltersVisible(false)

               globalContext.setItemInPreview(
                    getSelectedCandidatePlusPreviousAndNextCandidate(
                         item,
                         index,
                         globalContext.listingQuery.data!.data,
                         globalContext.listingQuery.data!.page,
                         globalContext.listingQuery.data!.totalPages
                    )
               )

               if (Array.isArray(globalContext.filters.filters?.annonces) && (globalContext.filters?.filters?.annonces?.length ?? 0) > 0) {
                    globalContext.checkItemAsSeen(item.candidat.id as number)
               }
          }
     }

     const SuivisListingInModal = useMemo(() => {
          return (
               <MyModal
                    title={<span>Échanges de {item.candidat.prenomNom}</span>}
                    show={true}
                    onCloseCallback={() =>
                         setIsSuivisModalOpen({
                              isOpen: false,
                              filters: [],
                         })
                    }
                    fullscreen
               >
                    <CandidatSuiviListing candidat_id={item.candidat.id as number} preFilterTypes={isSuivisModalOpen.filters} />
               </MyModal>
          )
     }, [isSuivisModalOpen])

     return (
          <SearchResultsListItemContext.Provider
               value={{
                    item,
                    isSuivisModalOpen,
                    setIsSuivisModalOpen,
               }}
          >
               <ListItem item={item} handleClickItemForPreview={handleClickItemForPreview} isItemSelected={isItemSelected} isItemLoading={isItemLoading} />

               {isSuivisModalOpen.isOpen && <>{SuivisListingInModal}</>}
          </SearchResultsListItemContext.Provider>
     )
}

export default CandidatsRechercheResultsListItem
